import BulkImportModal from '@/components/course-assignment/BulkImportModal';
import { cleanText } from '@/utils/sanitize';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import Modal from '@/components/ui/Modal';
import Pagination from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import {
  AlertCircle,
  BookOpen,
  Briefcase,
  Building, Calendar,
  Clock,
  Eye,
  ExternalLink,
  Info,
  Mail,
  Search,
  Send,
  Sparkles,
  TrendingUp,
  Upload,
  User as UserIcon,
  Users,
  X,
  XCircle,
  Shield
} from 'lucide-react';
import { Key, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '@/components/ui/Toast';
import { Link, useSearchParams } from 'react-router-dom';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { COURSE_ASSIGNMENT_PERMISSION, COURSE_ASSIGNMENT_ANALYTICS_PERMISSION } from '@/constants/constant-permissions';
import { ADMIN_COURSE_ASSIGNMENT_ANALYTICS } from '@/constants/constant-routes';
import { useForm } from 'react-hook-form';
import { Textarea } from '@/components/ui/Textarea';
import apiClient from '../../services/api';
import {
  Course,
  courseAssignmentDataService,
  courseAssignmentService,
  CourseAssignmentSettings,
  Group,
  User
} from '../../services/course-assignment.service';
import { courseService } from '../../services/course.service';
import { userService } from '../../services/user.service';

interface FilterFormData {
  recipientDepartmentFilter: string;
  selectedRoleFilter: string;
  roleAssignmentDepartmentFilter: string;
  roleAssignmentRoleFilter: string;
  reminderFrequency: number;
  assignmentMessage: string;
}

export default function CourseAssignment() {
  usePermissionRouteAccess([
    COURSE_ASSIGNMENT_PERMISSION.COURSE_ASSIGNMENT_READ,
    COURSE_ASSIGNMENT_PERMISSION.COURSE_ASSIGNMENT_WRITE,
  ]);
  const canWrite = useHasPermission([COURSE_ASSIGNMENT_PERMISSION.COURSE_ASSIGNMENT_WRITE]);
  const canViewAnalytics = useHasPermission([
    COURSE_ASSIGNMENT_ANALYTICS_PERMISSION.COURSE_ASSIGNMENT_ANALYTICS_READ,
    COURSE_ASSIGNMENT_ANALYTICS_PERMISSION.COURSE_ASSIGNMENT_ANALYTICS_WRITE,
  ]);

  const [assignmentType, setAssignmentType] = useState<'individual' | 'department' | 'role' | 'bulk'>('individual');
  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<Array<{ group_key: string, role_key: string, role_name?: string }>>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  // Search and filter for recipients
  const [recipientSearchQuery, setRecipientSearchQuery] = useState('');
  const [availableDepartments, setAvailableDepartments] = useState<any[]>([]);
  const [availableRolesForDepartment, setAvailableRolesForDepartment] = useState<any[]>([]);
  const [availableRolesForRoleAssignment, setAvailableRolesForRoleAssignment] = useState<any[]>([]);
  const [assignmentSettings, setAssignmentSettings] = useState<CourseAssignmentSettings>({
    is_mandatory: false,
    send_notification: true,
    enable_automatic_reminder: true,
    reminder_frequency_days: 7,
    deadline_date: undefined,
    message: undefined
  });
  const [deadlineDays, setDeadlineDays] = useState<number | undefined>(undefined);

  // Initialize react-hook-form for filters
  const { control, watch, setValue, getValues, trigger } = useForm<FilterFormData>({
    defaultValues: {
      recipientDepartmentFilter: 'all',
      selectedRoleFilter: 'all',
      roleAssignmentDepartmentFilter: '',
      roleAssignmentRoleFilter: '',
      reminderFrequency: 7,
      assignmentMessage: ''
    }
  });

  // Watch form values
  const recipientDepartmentFilter = watch('recipientDepartmentFilter');
  const selectedRoleFilter = watch('selectedRoleFilter');
  const roleAssignmentDepartmentFilter = watch('roleAssignmentDepartmentFilter');
  const roleAssignmentRoleFilter = watch('roleAssignmentRoleFilter');
  const reminderFrequency = watch('reminderFrequency');

  // Refs to track previous values for role assignment - to detect actual user changes
  const prevRoleAssignmentRoleFilter = useRef<string>('');
  const prevRoleAssignmentDepartmentFilter = useRef<string>('');

  // Sync reminderFrequency with assignmentSettings
  useEffect(() => {
    setAssignmentSettings(prev => ({ ...prev, reminder_frequency_days: reminderFrequency }));
  }, [reminderFrequency]);

  // Cache selected course data across pagination (so preview modal always has full data)
  const selectedCourseDataRef = useRef<Map<string, Course>>(new Map());

  // Real data from APIs
  const [courses, setCourses] = useState<Course[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  // Cache selected user data across pagination/search (so preview modal always has full data)
  const selectedUserDataRef = useRef<Map<string, User>>(new Map());
  useEffect(() => {
    users.forEach(user => {
      if (selectedUsers.includes(user._id)) selectedUserDataRef.current.set(user._id, user);
    });
  }, [users, selectedUsers]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);

  // Pagination states
  const [coursePagination, setCoursePagination] = useState({ page: 1, totalPages: 1, totalItems: 0, limit: 20 });
  const [userPagination, setUserPagination] = useState({ page: 1, totalPages: 1, totalItems: 0, limit: 20 });
  const [groupPagination, setGroupPagination] = useState({ page: 1, totalPages: 1, totalItems: 0, limit: 20 });
  const [, setRolePagination] = useState({ page: 1, totalPages: 1, totalItems: 0, limit: 20 });
  const [recentAssignments, setRecentAssignments] = useState<any[]>([]);
  const [recentAssignmentsLoading, setRecentAssignmentsLoading] = useState(false);

  // Duplicate detection state
  const [duplicateCheckResult, setDuplicateCheckResult] = useState<any>(null);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
  const [skipDuplicates, setSkipDuplicates] = useState(true); // Default to skip duplicates

  // Bulk import state
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);

  // Assignment actions state
  const [selectedAssignment, setSelectedAssignment] = useState<any>(null);
  const [showAssignmentDetails, setShowAssignmentDetails] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState<{id: string, title: string} | null>(null);

  // Course details modal state
  const [selectedCoursesModal, setSelectedCoursesModal] = useState<{courses: {_id: string, title: string}[], assignmentId: string} | null>(null);

  // User departments modal state
  const [selectedUserDepartments, setSelectedUserDepartments] = useState<{userName: string, email: string, groups: {group_key: string, group_name: string, roles?: {role_key: string, role_name: string}[]}[]} | null>(null);

  // Assigned users/groups/roles modal state for Recent Assignments
  const [selectedAssignedUsersModal, setSelectedAssignedUsersModal] = useState<{
    users?: {_id: string, name: string, email: string}[],
    groups?: {_id?: string, group_key?: string, name?: string, group_name?: string}[],
    roles?: {_id?: string, role_key?: string, name?: string, role_name?: string}[],
    type: 'USER' | 'GROUP' | 'ROLE'
  } | null>(null);

  // Skipped users modal state for Recent Assignments
  const [selectedSkippedUsersModal, setSelectedSkippedUsersModal] = useState<{
    skipped_users?: {user_id: string, user_email: string, user_name: string, course_id: string, reason: string}[],
    skipped_groups?: {group_key: string, group_name: string, course_id: string, reason: string}[],
    skipped_roles?: {group_key: string, role_key: string, role_name: string, course_id: string, reason: string}[]
  } | null>(null);

  const [searchParams] = useSearchParams();
  const [deadlineError, setDeadlineError] = useState('');
  // Load courses and recent assignments on component mount
  useEffect(() => {
    loadCourses();
    loadRecentAssignments();
  }, []);

  // Reload courses when search/filter changes (with debounce)
  useEffect(() => {
    const timer = setTimeout(() => {
      // Reset to page 1 when search changes
      setCoursePagination(prev => ({ ...prev, page: 1 }));
      loadCourses(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load recipients based on assignment type
  useEffect(() => {
    if (assignmentType === 'individual') {
      loadUsers();
    } else if (assignmentType === 'department') {
      loadGroups();
    } else if (assignmentType === 'role') {
      // For role assignment, we use department-role dropdown approach
      // No need to load all roles
    } else if (assignmentType === 'bulk') {
      // Bulk import tab will show information page with button to open modal
      // Don't auto-open modal here
    }
  }, [assignmentType]);

  // Reload users when search/filter changes (with debounce)
  useEffect(() => {
    if (assignmentType === 'individual') {
      const timer = setTimeout(() => {
        // Reset to page 1 when search changes
        setUserPagination(prev => ({ ...prev, page: 1 }));
        loadUsers(1);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [recipientSearchQuery, recipientDepartmentFilter, selectedRoleFilter]);

  // Load all groups once to populate department filter
  useEffect(() => {
    loadAllGroupsForFilter();
  }, []);

  const loadAllGroupsForFilter = async () => {
    try {
      const response = await userService.getGroupsDropdown();
      if (response.success && response.data) {
        // Response schema changed: data is now directly an array, not data.data
        setAvailableDepartments(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load departments for filter');
    }
  };

  //Get the user ID when clicking on Employee Progress, then go to Assign Course, and navigate to Course Assignment.
  useEffect(() => {
    const userIdFromUrl = searchParams.get('user_id');
    if (userIdFromUrl) {
      //  Automatically switch to the 'individual' assignment tab
      setAssignmentType('individual');

      setSelectedUsers([userIdFromUrl]);
    }
  }, [searchParams]);

  useEffect(() => {
    const groupIdFromUrl = searchParams.get('group_id');
    if (groupIdFromUrl) {
      //  Automatically switch to the 'dept' assignment tab
      setAssignmentType('department');

      setSelectedDepartments([groupIdFromUrl]);
    }
  }, [searchParams]);

  // Load roles for selected department
  useEffect(() => {
    if (recipientDepartmentFilter !== 'all') {
      loadRolesForDepartment(recipientDepartmentFilter);
    } else {
      setAvailableRolesForDepartment([]);
      setValue('selectedRoleFilter', 'all');
    }
  }, [recipientDepartmentFilter, setValue]);

  const loadRolesForDepartment = async (groupKey: string) => {
    try {
      const response = await apiClient.get(`/group-role-map/${groupKey}`);
      if (response.data.success && response.data.data) {
        const roles = response.data.data.roles || [];
        setAvailableRolesForDepartment(roles);
      }
    } catch (error) {
      console.error('Failed to load roles for department:', error);
      setAvailableRolesForDepartment([]);
    }
  };

  // Load roles for selected department in role assignment
  useEffect(() => {
    // IMPORTANT: Clear available roles FIRST to prevent auto-selection of previous role
    setAvailableRolesForRoleAssignment([]);

    // Always reset role filter when department changes - using undefined to fully reset
    setValue('roleAssignmentRoleFilter', '', { shouldValidate: false, shouldDirty: false });

    if (roleAssignmentDepartmentFilter && roleAssignmentDepartmentFilter !== '') {
      // Small delay to ensure form state is cleared before loading new roles
      const timeoutId = setTimeout(() => {
        loadRolesForRoleAssignment(roleAssignmentDepartmentFilter);
      }, 0);
      return () => clearTimeout(timeoutId);
    } else {
      // Don't clear selectedRoles - allow multi-department role selection
    }
  }, [roleAssignmentDepartmentFilter, setValue]);

  const loadRolesForRoleAssignment = async (groupKey: string) => {
    try {
      const response = await apiClient.get(`/group-role-map/${groupKey}`);
      if (response.data.success && response.data.data) {
        const roles = response.data.data.roles || [];
        setAvailableRolesForRoleAssignment(roles);
      }
    } catch (error) {
      console.error('Failed to load roles for role assignment:', error);
      setAvailableRolesForRoleAssignment([]);
    }
  };

  // Handle role selection from dropdown in role assignment
  useEffect(() => {
    // STRICT CHECK: Only add role if user explicitly selected it (value changed from previous)
    const roleFilterChanged = prevRoleAssignmentRoleFilter.current !== roleAssignmentRoleFilter;
    const departmentIsSame = prevRoleAssignmentDepartmentFilter.current === roleAssignmentDepartmentFilter;

    // Update refs for next comparison
    prevRoleAssignmentRoleFilter.current = roleAssignmentRoleFilter;
    prevRoleAssignmentDepartmentFilter.current = roleAssignmentDepartmentFilter;

    // Only proceed if:
    // 1. Role filter actually changed (user selected something)
    // 2. Department is the same (not switching departments)
    // 3. New role value is not empty
    if (
      !roleFilterChanged ||
      !departmentIsSame ||
      !roleAssignmentRoleFilter ||
      roleAssignmentRoleFilter === '' ||
      roleAssignmentRoleFilter === 'all' ||
      availableRolesForRoleAssignment.length === 0 ||
      !roleAssignmentDepartmentFilter ||
      roleAssignmentDepartmentFilter === '' ||
      roleAssignmentDepartmentFilter === 'all'
    ) {
      return; // Don't add role
    }

    // Verify the role actually exists in the current department's available roles
    const selectedRole = availableRolesForRoleAssignment.find(r => r.role_key === roleAssignmentRoleFilter);

    // Only proceed if the role exists in the current department
    if (!selectedRole) {
      console.log('Role not found in current department, skipping selection');
      return;
    }

    // Check if this role from this department is already selected
    setSelectedRoles(prev => {
      const roleExists = prev.some(
        r => r.group_key === roleAssignmentDepartmentFilter && r.role_key === roleAssignmentRoleFilter
      );

      if (roleExists) {
        return prev; // Already exists, no change
      }

      // Add the new role - ONLY if it passed all checks above
      console.log('Adding role:', roleAssignmentRoleFilter, 'from department:', roleAssignmentDepartmentFilter);
      return [...prev, {
        group_key: roleAssignmentDepartmentFilter,
        role_key: roleAssignmentRoleFilter,
        role_name: selectedRole.role_name
      }];
    });
  }, [roleAssignmentRoleFilter, roleAssignmentDepartmentFilter, availableRolesForRoleAssignment]);

  // Reload groups when search changes (with debounce)
  useEffect(() => {
    if (assignmentType === 'department') {
      const timer = setTimeout(() => {
        // Reset to page 1 when search changes
        setGroupPagination(prev => ({ ...prev, page: 1 }));
        loadGroups(1);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [recipientSearchQuery]);

  useEffect(() => {
    setRecipientSearchQuery('');
    // setRecipientDepartmentFilter('all');
    // setSelectedRoleFilter('all');
    setSearchQuery('');

    // Reset role assignment filters when switching assignment types
    setValue('roleAssignmentDepartmentFilter', '');
    setValue('roleAssignmentRoleFilter', '');

    // Reset pagination when assignment type changes
    setUserPagination(prev => ({ ...prev, page: 1 }));
    setGroupPagination(prev => ({ ...prev, page: 1 }));
    setRolePagination(prev => ({ ...prev, page: 1 }));
  }, [assignmentType, setValue]);
  const loadCourses = async (page: number = coursePagination.page, limit: number = coursePagination.limit) => {
    try {
      setLoading(true);

      // Use minimal course list API with only published courses
      const response = await courseService.getMinimalCourses({
        stage: 'published',  // Only get published courses
        search: searchQuery || undefined,
        limit: limit,
        page: page,
        sortBy: 'updatedAt',
        sortOrder: 'desc'
      });

      if (response && response.courses) {
        let courses = response.courses.map((course: { _id: any; title: any; description: any; difficulty: any; module_count: any; thumbnail_image_url: any; banner_image_url: any; users_assigned: any; rating: any; estimated_duration: any; source_type: any; }) => ({
          _id: course._id,
          id: course._id,
          title: course.title,
          description: course.description,
          category_name: '', // Not available in minimal response
          difficulty: course.difficulty,
          module_count: course.module_count,
          thumbnail_image_url: course.thumbnail_image_url,
          banner_image_url: course.banner_image_url,
          users_assigned: course.users_assigned,
          rating: course.rating,
          estimated_duration: course.estimated_duration,
          source_type: course.source_type // 'scorm_imported' for SCORM courses
        }));

        setCourses(courses);

        // Update pagination state
        setCoursePagination(prev => ({
          ...prev,
          page: page,
          limit: limit,
          totalPages: response.pagination?.totalPages || Math.ceil((response.total || courses.length) / limit),
          totalItems: response.pagination?.totalItems || response.total || courses.length
        }));
      }
    } catch (error) {
      console.error('Failed to load courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async (page: number = userPagination.page) => {
    try {
      setUsersLoading(true);
      const params: any = {
        status: 'ACTIVE',  // Only load active users by default (using UserStatusEnum.ACTIVE)
        page: page,
        limit: userPagination.limit
      };

      // Add search parameter (backend uses 'search')
      if (recipientSearchQuery) {
        params.search = recipientSearchQuery;
      }

      // Add group filter if a department is selected
      if (recipientDepartmentFilter !== 'all') {
        params.group_key = recipientDepartmentFilter;
      }

      // Add role filter if a role is selected
      if (selectedRoleFilter !== 'all') {
        params.role_key = selectedRoleFilter;
      }

      const response = await courseAssignmentDataService.loadUsers(params);
      if (response.status === 'success' && response.data) {
        setUsers(response.data.data);

        // Update pagination state
        setUserPagination(prev => ({
          ...prev,
          page: page,
          totalPages: response.data.pagination?.totalPages || Math.ceil((response.data.total || response.data.data.length) / prev.limit),
          totalItems: response.data.pagination?.totalItems || response.data.total || response.data.data.length
        }));
      }
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

const loadGroups = async (page: number = groupPagination.page) => {
  try {
    setGroupsLoading(true);

    const skip = (page - 1) * groupPagination.limit;

    const body: any = {
      skip: skip,
      limit: groupPagination.limit
    };

    if (recipientSearchQuery) {
      body.group_name = recipientSearchQuery;
    }

    const response = await courseAssignmentDataService.loadGroups(body);

    if (response.status === 'success' && response.data) {
      const groupsData = response.data.groups || [];
      setGroups(groupsData);

      setGroupPagination(prev => ({
        ...prev,
        page: page,
        totalPages:
          Math.ceil((response.data.total || groupsData.length) / prev.limit),
        totalItems: response.data.total || groupsData.length
      }));
    } else {
      console.warn('Groups response missing data:', response);
      setGroups([]);
    }
  } catch (error) {
    console.error('Error loading groups:', error);
    toast.error('Failed to load groups');
  } finally {
    setGroupsLoading(false);
  }
};



  // Handle bulk import success
  const handleBulkImportSuccess = () => {
    setShowBulkImportModal(false);
    // Stay on bulk tab after successful import
    loadRecentAssignments(); // Reload assignments list
    toast.success('Bulk import completed successfully!');
  };

  const handleSendReminder = async (assignmentId: string) => {
  try {
    setActionLoading(assignmentId);
 
    const reminderData = await courseAssignmentService.sendReminderNotifications(assignmentId);
 
    const { message, failure_count } = reminderData;

    if (failure_count === 0) {
      toast.success(message);
    } else {
      toast.warning(message);
    }
 
  } catch (error: any) {
    console.error('Failed to send reminders:', error);
    toast.error(error.message || 'Failed to send reminders');
  } finally {
    setActionLoading(null);
  }
};
  const handleConfirmDelete = async () => {
    if (!assignmentToDelete) return;

    try {
      setActionLoading(assignmentToDelete.id);
      await courseAssignmentService.deleteAssignment(assignmentToDelete.id);
      toast.success('Assignment deleted successfully!');
      loadRecentAssignments(); // Reload the list
      setShowDeleteModal(false);
      setAssignmentToDelete(null);
    } catch (error: any) {
      console.error('Failed to delete assignment:', error);
      toast.error(error.message || 'Failed to delete assignment');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setAssignmentToDelete(null);
  };

  // Load recent assignments from API (limit to 5) - using same list API as analytics page
  const loadRecentAssignments = async () => {
    try {
      setRecentAssignmentsLoading(true);
      const response: any = await courseAssignmentService.listAssignments({
        limit: 5,
        page: 1
      });
      console.log('Recent assignments response:', response);
      // API returns data in response.data array
      if (response && response.data && Array.isArray(response.data)) {
        setRecentAssignments(response.data);
      } else {
        setRecentAssignments([]);
      }
    } catch (error) {
      console.error('Failed to load recent assignments:', error);
      setRecentAssignments([]);
    } finally {
      setRecentAssignmentsLoading(false);
    }
  };

  const handleSelectCourse = (courseId: string) => {
    setSelectedCourses(prev => {
      if (prev.includes(courseId)) {
        selectedCourseDataRef.current.delete(courseId);
        return prev.filter(id => id !== courseId);
      } else {
        const course = courses.find(c => c._id === courseId);
        if (course) selectedCourseDataRef.current.set(courseId, course);
        return [...prev, courseId];
      }
    });
  };

  // Selected users may not be on the current page of results, so fall back to the cache
  const getSelectedUser = (userId: string) =>
    users.find(u => u._id === userId) || selectedUserDataRef.current.get(userId);

  const handleSelectUser = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectDepartment = (deptId: string) => {
    setSelectedDepartments(prev =>
      prev.includes(deptId) ? [] : [deptId]
    );
  };

  const handleSelectRole = (groupKey: string, roleKey: string) => {
    setSelectedRoles(prev =>
      prev.filter(r => !(r.group_key === groupKey && r.role_key === roleKey))
    );
  };

  const handlePreviewClick = async () => {
    // Reset previous duplicate check
    setDuplicateCheckResult(null);

    // Validate selections
    if (!selectedCourses || selectedCourses.length === 0) {
      toast.error('Please select at least one course');
      return;
    }

    const targetIds = assignmentType === 'department' ? selectedDepartments
      : assignmentType === 'role' ? selectedRoles.map(r => ({ group_key: r.group_key, role_key: r.role_key }))
        : selectedUsers;

    if (!targetIds || targetIds.length === 0) {
      toast.error('Please select recipients for the assignment');
      return;
    }

    // Check for duplicates
    setIsCheckingDuplicates(true);
    try {
      const checkType = assignmentType === 'department' ? 'groups'
        : assignmentType === 'role' ? 'roles'
          : 'users';

      const targetIdStrings = assignmentType === 'role'
        ? selectedRoles.map(r => `${r.group_key}_${r.role_key}`)
        : targetIds as string[];

      const duplicateCheck = await courseAssignmentService.checkForDuplicates({
        type: checkType as 'users' | 'groups' | 'roles',
        targetIds: targetIdStrings,
        courseIds: selectedCourses
      });

      setDuplicateCheckResult(duplicateCheck);

      // Show warning if duplicates found
      if (duplicateCheck.hasConflicts) {
        // Count unique targets (users/departments/roles), not total conflicts
        const uniqueTargets = new Set(duplicateCheck.conflicts.map((c: any) => c.targetId));
        const targetCount = uniqueTargets.size;
        const targetLabel = assignmentType === 'department' ? 'department'
          : assignmentType === 'role' ? 'role'
          : 'user';
        toast.warning(`${targetCount} ${targetLabel}${targetCount > 1 ? 's have' : ' has'} duplicate courses. Review in preview.`);
      }
    } catch (error) {
      console.error('Failed to check for duplicates:', error);
      toast.error('Failed to check for duplicate assignments');
    } finally {
      setIsCheckingDuplicates(false);
    }

    // Show preview modal
    setShowPreview(true);
  };
const handleDeadlineDaysChange = (e: { target: { value: string; }; }) => {
  const inputValue = e.target.value;

  // Clear error when user starts typing
  if (deadlineError) {
    setDeadlineError('');
  }

  // Allow empty input (clear the field)
  if (inputValue === '') {
    setDeadlineDays(undefined);
    setAssignmentSettings(prev => ({
      ...prev,
      deadline_date: undefined
    }));
    return;
  }

  const days = parseInt(inputValue);

  // Validate: must be a positive number
  if (isNaN(days) || days <= 0) {
    setDeadlineError('Deadline must be a positive number.');
    setDeadlineDays(undefined);
    setAssignmentSettings(prev => ({
      ...prev,
      deadline_date: undefined
    }));
    return;
  }

  if (days > 365) {
    setDeadlineError('Deadline cannot exceed 365 days.');
    setDeadlineDays(undefined);
    setAssignmentSettings(prev => ({
      ...prev,
      deadline_date: undefined
    }));
    return;
  }

  setDeadlineDays(days);

  // Calculate deadline date from days
  const deadlineDate = new Date();
  deadlineDate.setDate(deadlineDate.getDate() + days);
  deadlineDate.setHours(23, 59, 59, 999);

  setAssignmentSettings(prev => ({
    ...prev,
    deadline_date: deadlineDate.toISOString()
  }));
};

const handleDeadlineDaysBlur = () => {
  if (deadlineDays !== undefined && deadlineDays <= 0) {
    setDeadlineError('Deadline must be a positive number.');
  } else {
    setDeadlineError('');
  }
};
  const handleAssignCourses = async () => {
    if (!selectedCourses || selectedCourses.length === 0) {
      toast.error('Please select at least one course');
      return;
    }

    const targetIds = assignmentType === 'department' ? selectedDepartments
      : assignmentType === 'role' ? selectedRoles
        : selectedUsers;

    if (!targetIds || targetIds.length === 0) {
      toast.error('Please select recipients for the assignment');
      return;
    }

    // Runs the shared Textarea's clean-text rule; the field shows its own error
    if (!(await trigger('assignmentMessage'))) {
      toast.error('Please fix the message before assigning');
      return;
    }

    setIsAssigning(true);
    try {
      // Don't filter users on frontend - send all selected users
      let filteredTargetIds = assignmentType === 'department' ? selectedDepartments
        : assignmentType === 'role' ? selectedRoles
          : selectedUsers;

      // Filter courses based on duplicate detection and skipDuplicates setting
      let coursesToAssign = selectedCourses;

      if (skipDuplicates && duplicateCheckResult?.hasConflicts) {
        if (assignmentType === 'role') {
          // For role assignments, filter out roles that have conflicts with ALL selected courses
          // Build a map of role -> courses with conflicts
          const roleConflictsMap = new Map<string, Set<string>>();

          duplicateCheckResult.conflicts.forEach((conflict: any) => {
            const roleId = conflict.targetId; // Format: "group_key_role_key"
            if (!roleConflictsMap.has(roleId)) {
              roleConflictsMap.set(roleId, new Set());
            }
            roleConflictsMap.get(roleId)!.add(conflict.courseId);
          });

          // Filter out roles that have conflicts with ALL selected courses
          filteredTargetIds = selectedRoles.filter(roleObj => {
            const roleId = `${roleObj.group_key}_${roleObj.role_key}`;
            const conflictingCourses = roleConflictsMap.get(roleId);

            // If this role has no conflicts, include it
            if (!conflictingCourses) {
              return true;
            }

            // If this role has conflicts with ALL selected courses, exclude it
            const hasAllConflicts = selectedCourses.every(courseId => conflictingCourses.has(courseId));
            return !hasAllConflicts;
          });

          if (filteredTargetIds.length === 0) {
            toast.warning('All selected courses are already assigned to all selected roles. No new assignments to create.');
            setIsAssigning(false);
            return;
          }

          console.log('[Assignment] Filtered roles:', {
            original: selectedRoles.length,
            filtered: filteredTargetIds.length,
            excluded: selectedRoles.length - filteredTargetIds.length
          });
        } else if (assignmentType === 'department') {
          // For department assignments, filter out departments that have conflicts with ALL selected courses
          // Build a map of department -> courses with conflicts
          const deptConflictsMap = new Map<string, Set<string>>();

          duplicateCheckResult.conflicts.forEach((conflict: any) => {
            const deptId = conflict.targetId;
            if (!deptConflictsMap.has(deptId)) {
              deptConflictsMap.set(deptId, new Set());
            }
            deptConflictsMap.get(deptId)!.add(conflict.courseId);
          });

          // Filter out departments that have conflicts with ALL selected courses
          filteredTargetIds = selectedDepartments.filter(deptId => {
            const conflictingCourses = deptConflictsMap.get(deptId);

            // If this department has no conflicts, include it
            if (!conflictingCourses) {
              return true;
            }

            // If this department has conflicts with ALL selected courses, exclude it
            const hasAllConflicts = selectedCourses.every(courseId => conflictingCourses.has(courseId));
            return !hasAllConflicts;
          });

          if (filteredTargetIds.length === 0) {
            toast.warning('All selected courses are already assigned to all selected departments. No new assignments to create.');
            setIsAssigning(false);
            return;
          }

          console.log('[Assignment] Filtered departments:', {
            original: selectedDepartments.length,
            filtered: filteredTargetIds.length,
            excluded: selectedDepartments.length - filteredTargetIds.length
          });
        } else {
          // For user assignments, filter out users that have conflicts with ALL selected courses
          // Build a map of user -> courses with conflicts
          const userConflictsMap = new Map<string, Set<string>>();

          duplicateCheckResult.conflicts.forEach((conflict: any) => {
            const userId = conflict.targetId;
            if (!userConflictsMap.has(userId)) {
              userConflictsMap.set(userId, new Set());
            }
            userConflictsMap.get(userId)!.add(conflict.courseId);
          });

          // Filter out users that have conflicts with ALL selected courses
          filteredTargetIds = selectedUsers.filter(userId => {
            const conflictingCourses = userConflictsMap.get(userId);

            // If this user has no conflicts, include them
            if (!conflictingCourses) {
              return true;
            }

            // If this user has conflicts with ALL selected courses, exclude them
            const hasAllConflicts = selectedCourses.every(courseId => conflictingCourses.has(courseId));
            return !hasAllConflicts;
          });

          if (filteredTargetIds.length === 0) {
            toast.warning('All selected courses are already assigned to all selected users. No new assignments to create.');
            setIsAssigning(false);
            return;
          }

          console.log('[Assignment] Filtered users:', {
            original: selectedUsers.length,
            filtered: filteredTargetIds.length,
            excluded: selectedUsers.length - filteredTargetIds.length
          });
        }
      }

      // Build the request object based on assignment type
      const request: any = {
        course_ids: coursesToAssign,
        is_mandatory: assignmentSettings.is_mandatory,
        deadline_date: assignmentSettings.deadline_date || null,
        deadline_days: deadlineDays || null,
        send_notification: assignmentSettings.send_notification,
        // Only send reminder fields if notification is enabled
        enable_automatic_reminder: assignmentSettings.send_notification ? assignmentSettings.enable_automatic_reminder : false,
        message: cleanText(getValues('assignmentMessage')) || undefined,
        skip_duplicates: skipDuplicates // Let backend handle per-user/group/role duplicate detection
      };

      // Only include reminder_frequency_days if both notification and automatic reminder are enabled
      if (assignmentSettings.send_notification && assignmentSettings.enable_automatic_reminder) {
        request.reminder_frequency_days = (assignmentSettings.reminder_frequency_days ?? 0) > 0 ? assignmentSettings.reminder_frequency_days : 7;
      }

      let response;

      if (assignmentType === 'department') {
       const groupKeys = filteredTargetIds.map(groupId => {
          const group = groups.find(g => g._id === groupId);
          return group?.group_key;
        }).filter(Boolean);

        if (groupKeys.length !== filteredTargetIds.length) {
            console.error("Could not find a matching group key for all selected group IDs.");
            toast.error("An error occurred while preparing department data. Please try again.");
            setIsAssigning(false);
            return;
        }

        request.group_keys = groupKeys;

        // Add skipped_groups to payload if there are conflicts
        if (skipDuplicates && duplicateCheckResult?.hasConflicts) {
          const skippedGroups = duplicateCheckResult.conflicts.map((conflict: any) => {
            const group = groups.find(g => g._id === conflict.targetId);
            return {
              group_key: group?.group_key || conflict.targetId,
              group_name: group?.group_name || 'Unknown Group',
              course_id: conflict.courseId,
              reason: 'Group already has this course assigned'
            };
          });
          if (skippedGroups.length > 0) {
            request.skipped_groups = skippedGroups;
          }
        }

        response = await courseAssignmentService.assignCoursesToGroups(request);
      } else if (assignmentType === 'role') {
        // For roles, send array of objects with group_key and role_key
        // filteredTargetIds is already an array of { group_key, role_key, role_name? }
        const roleTargets = filteredTargetIds as Array<{ group_key: string; role_key: string; role_name?: string }>;
        const roleAssignments = roleTargets.map(r => ({
          group_key: r.group_key,
          role_key: r.role_key
        }));

        if (roleAssignments.length === 0) {
          toast.error('Please select at least one role for assignment');
          setIsAssigning(false);
          return;
        }

        // Send as role_keys field (backend expects this field name)
        request.role_keys = roleAssignments;

        // Add skipped_roles to payload if there are conflicts
        if (skipDuplicates && duplicateCheckResult?.hasConflicts) {
          const skippedRoles = duplicateCheckResult.conflicts.map((conflict: any) => {
            // targetId format is "group_key_role_key"
            const [groupKey, ...roleKeyParts] = conflict.targetId.split('_');
            const roleKey = roleKeyParts.join('_');
            const roleObj = selectedRoles.find(r => r.group_key === groupKey && r.role_key === roleKey);
            return {
              group_key: groupKey,
              role_key: roleKey,
              role_name: roleObj?.role_name || roleKey,
              course_id: conflict.courseId,
              reason: 'Role already has this course assigned'
            };
          });
          if (skippedRoles.length > 0) {
            request.skipped_roles = skippedRoles;
          }
        }

        response = await courseAssignmentService.assignCoursesToRoles(request);
      } else {
        // For users, use user_ids
        request.user_ids = filteredTargetIds;

        // Add skipped_users to payload if there are conflicts
        if (skipDuplicates && duplicateCheckResult?.hasConflicts) {
          const skippedUsers = duplicateCheckResult.conflicts.map((conflict: any) => {
            const user = getSelectedUser(conflict.targetId);
            return {
              user_id: conflict.targetId,
              user_email: user?.email || 'Unknown',
              user_name: user?.full_name || user?.given_name || 'Unknown User',
              course_id: conflict.courseId,
              reason: 'User already has this course assigned'
            };
          });
          if (skippedUsers.length > 0) {
            request.skipped_users = skippedUsers;
          }
        }

        response = await courseAssignmentService.assignCoursesToUsers(request);
      }


      // The response is directly the AssignmentResponse object
      if (response) {
        // Use the message from the response or construct a default one
        let successMessage = response.message ||
          `Successfully assigned ${response.courses_assigned} course(s) to ${response.total_users_assigned} user(s)!`;

        // Add information about skipped items if we filtered them on frontend
        if (skipDuplicates && duplicateCheckResult?.hasConflicts) {
          if (assignmentType === 'role') {
            // For roles, show how many roles were excluded
            const excludedRolesCount = selectedRoles.length - filteredTargetIds.length;
            if (excludedRolesCount > 0) {
              successMessage += ` (${excludedRolesCount} role${excludedRolesCount > 1 ? 's' : ''} with complete conflicts excluded)`;
            }
          } else if (assignmentType === 'department') {
            // For departments, show how many departments were excluded
            const excludedDeptsCount = selectedDepartments.length - filteredTargetIds.length;
            if (excludedDeptsCount > 0) {
              successMessage += ` (${excludedDeptsCount} department${excludedDeptsCount > 1 ? 's' : ''} with complete conflicts excluded)`;
            }
          } else {
            // For users, show how many users were excluded
            const excludedUsersCount = selectedUsers.length - filteredTargetIds.length;
            if (excludedUsersCount > 0) {
              successMessage += ` (${excludedUsersCount} user${excludedUsersCount > 1 ? 's' : ''} with complete conflicts excluded)`;
            }
          }
        }

        toast.success(successMessage);

        // Handle skipped items from backend response (conflicts/duplicates)
        if (response.skipped_users && response.skipped_users.length > 0) {
          const skippedCount = response.skipped_users.length;
          toast.warning(`${skippedCount} user${skippedCount > 1 ? 's' : ''} skipped due to existing assignments`);
        }
        if (response.skipped_groups && response.skipped_groups.length > 0) {
          const skippedCount = response.skipped_groups.length;
          toast.warning(`${skippedCount} group${skippedCount > 1 ? 's' : ''} skipped due to existing assignments`);
        }
        if (response.skipped_roles && response.skipped_roles.length > 0) {
          const skippedCount = response.skipped_roles.length;
          toast.warning(`${skippedCount} role${skippedCount > 1 ? 's' : ''} skipped due to existing assignments`);
        }

        if (response.notification_failures && response.notification_failures > 0) {
          toast.warning(`${response.notification_failures} notifications failed to send`);
        }

        // Reset form state and close popup after successful assignment
        resetFormState();
        setShowPreview(false);

        // Reload recent assignments
        loadRecentAssignments();
      } else {
        toast.error('Failed to assign courses');
      }
    } catch (error: any) {
      console.error('Assignment error:', error);
      const errorMessage = error?.response?.data?.error?.error_description || error.message || 'Failed to assign courses';
      toast.error(errorMessage);
    } finally {
      setIsAssigning(false);
    }
  };

  // Reset assignment form state
  const resetFormState = useCallback(() => {
    setSelectedCourses([]);
    selectedCourseDataRef.current.clear();
    setSelectedUsers([]);
    selectedUserDataRef.current.clear();
    setSelectedDepartments([]);
    setSelectedRoles([]);
    setAssignmentSettings({
      is_mandatory: false,
      send_notification: true,
      enable_automatic_reminder: true,
      reminder_frequency_days: 7,
      deadline_date: undefined,
      message: undefined
    });
    // Reset duplicate check state
    setDuplicateCheckResult(null);
    setSkipDuplicates(true);
    setValue('assignmentMessage', '');
    // Reset role assignment form fields
    setValue('roleAssignmentDepartmentFilter', '');
    setValue('roleAssignmentRoleFilter', '');
    // Reset refs to prevent auto-selection on next use
    prevRoleAssignmentRoleFilter.current = '';
    prevRoleAssignmentDepartmentFilter.current = '';
    // Clear available roles for role assignment
    setAvailableRolesForRoleAssignment([]);
  }, [setValue]);

  // Pagination handlers
  const handleCoursePageChange = (page: number) => {
    setCoursePagination(prev => ({ ...prev, page }));
    loadCourses(page);
  };

  const handleCourseLimitChange = (limit: number) => {
    setCoursePagination(prev => ({ ...prev, limit, page: 1 }));
    loadCourses(1, limit);
  };

  const handleUserPageChange = (page: number) => {
    setUserPagination(prev => ({ ...prev, page }));
    loadUsers(page);
  };

  const handleUserLimitChange = (limit: number) => {
    setUserPagination(prev => ({ ...prev, limit, page: 1 }));
    loadUsers(1);
  };

  const handleGroupPageChange = (page: number) => {
    setGroupPagination(prev => ({ ...prev, page }));
    loadGroups(page);
  };

  const handleGroupLimitChange = (limit: number) => {
    setGroupPagination(prev => ({ ...prev, limit, page: 1 }));
    loadGroups(1);
  };

const getTargetName = (target: any, type: string): string => {
  switch (type) {
    case 'USER':
      return `${target.given_name || ''} ${target.family_name || ''}`.trim() || 'Unknown User';
    case 'ROLE':
      return target.role_name || 'Unknown Role';
    case 'GROUP':
      return target.group_name || 'Unknown Group';
    default:
      return 'Unknown Target';
  }
};
//Target key for while map find the id
const getTargetKey = (target: any, index: number): Key => {
  return target._id || target.role_key || target.group_key || index;
};
  return (
    <div className="space-y-6">
      {/* Info Banner about Learning Paths */}
      {/* <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Sparkles className="w-5 h-5 text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-gray-900 mb-1">New! Learning Paths Available</h3>
            <p className="text-sm text-gray-600">
              Create structured learning journeys with multiple courses. Learning paths provide a guided sequence of courses for specific roles or skills.
            </p>
            <Link
              to="/corporate/learning-paths"
              className="inline-flex items-center gap-2 mt-2 text-sm font-medium text-purple-600 hover:text-purple-700"
            >
              Explore Learning Paths
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <button className="text-gray-400 hover:text-gray-600">
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      </div> */}

      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl p-4 lg:p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-xl lg:text-2xl font-bold mb-2">Course Assignment</h1>
            <p className="text-indigo-100 text-sm lg:text-base">
              Assign courses to individuals, teams, or entire departments
            </p>
          </div>
          <div className="flex gap-2 lg:gap-3">
            <Link
              to={canViewAnalytics ? ADMIN_COURSE_ASSIGNMENT_ANALYTICS : "#"}
              onClick={(e) => !canViewAnalytics && e.preventDefault()}
              title={!canViewAnalytics ? 'You do not have permission to view analytics' : undefined}
              className={`flex items-center gap-2 px-3 lg:px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg transition-colors text-sm ${canViewAnalytics ? 'hover:bg-white/30' : 'opacity-50 cursor-not-allowed'}`}
            >
              <TrendingUp className="w-4 lg:w-5 h-4 lg:h-5" />
              <span className="hidden sm:inline">View Analytics</span>
              <span className="sm:hidden">Analytics</span>
            </Link>
            {/* <Link
              to="/corporate/learning-paths"
              className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors"
            >
              <TrendingUp className="w-5 h-5" />
              <span className="hidden sm:inline">Learning Paths</span>
            </Link> */}
            {/* <button className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors">
              <Upload className="w-5 h-5" />
              <span className="hidden sm:inline">Import CSV</span>
            </button> */}
            {/* <button className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors">
              <Download className="w-5 h-5" />
              <span className="hidden sm:inline">Export</span>
            </button> */}
          </div>
        </div>

        {/* Assignment Type Selector */}
        <div className="flex flex-wrap gap-2 mt-6">
          <button
            onClick={() => {
              setAssignmentType('individual');
              setRecipientSearchQuery('');
            }}
            className={`px-3 lg:px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm ${assignmentType === 'individual'
              ? 'bg-white text-indigo-600'
              : 'bg-white/20 text-white hover:bg-white/30'
              }`}
          >
            <UserIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Individual</span>
            <span className="sm:hidden">Users</span>
          </button>
          <button
            onClick={() => {
              setAssignmentType('department');
              setRecipientSearchQuery('');
            }}
            className={`px-3 lg:px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm ${assignmentType === 'department'
              ? 'bg-white text-indigo-600'
              : 'bg-white/20 text-white hover:bg-white/30'
              }`}
          >
            <Building className="w-4 h-4" />
            <span className="hidden sm:inline">Department</span>
            <span className="sm:hidden">Dept</span>
          </button>
          <button
            onClick={() => {
              setAssignmentType('role');
              setRecipientSearchQuery('');
            }}
            className={`px-3 lg:px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm ${assignmentType === 'role'
              ? 'bg-white text-indigo-600'
              : 'bg-white/20 text-white hover:bg-white/30'
              }`}
          >
            <Briefcase className="w-4 h-4" />
            <span className="hidden sm:inline">Role-Based</span>
            <span className="sm:hidden">Roles</span>
          </button>
          <button
            onClick={() => {
              setAssignmentType('bulk');
              setRecipientSearchQuery('');
            }}
            className={`px-3 lg:px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm ${assignmentType === 'bulk'
              ? 'bg-white text-indigo-600'
              : 'bg-white/20 text-white hover:bg-white/30'
              }`}
          >
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Bulk Import</span>
            <span className="sm:hidden">Bulk</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6">
        {/* Course Selection */}
        <div className="space-y-4">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold text-gray-900">Select Courses</h2>
                <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full border border-green-300">
                  🟢 Published Only
                </span>
              </div>
              <span className="text-sm text-gray-500">
                {selectedCourses?.length || 0} selected
              </span>
            </div>

            {/* Filters */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search courses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            {/* Course List */}
            <div className="space-y-3 max-h-[753px] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-gray-500">Loading courses...</div>
                </div>
              ) : courses.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-gray-500">No courses available</div>
                </div>
              ) : (
                courses.map((course) => (
                  <label
                    key={course._id}
                    className="block p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    {/* Mobile Layout */}
                    <div className="flex items-start gap-3 md:hidden">
                      <input
                        type="checkbox"
                        checked={selectedCourses.includes(course._id)}
                        onChange={() => handleSelectCourse(course._id)}
                        className="mt-1 rounded border-gray-300 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-3">
                          {course.thumbnail_image_url ? (
                            <img
                              src={course.thumbnail_image_url}
                              alt={course.title}
                              className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                e.currentTarget.nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                          ) : null}
                          <div className={`w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center text-white flex-shrink-0 ${course.thumbnail_image_url ? 'hidden' : ''}`}>
                            <BookOpen className="w-6 h-6" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-gray-900 text-sm line-clamp-2">{course.title}</h3>
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-xs text-gray-500">
                                {course.module_count} modules
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${course.difficulty?.toLowerCase() === 'beginner' ? 'bg-green-100 text-green-700' :
                                course.difficulty?.toLowerCase() === 'intermediate' ? 'bg-yellow-100 text-yellow-700' :
                                  course.difficulty?.toLowerCase() === 'advanced' ? 'bg-orange-100 text-orange-700' :
                                    'bg-purple-100 text-purple-700'
                                }`}>
                                {course.difficulty?.toUpperCase() || 'N/A'}
                              </span>
                            </div>
                            {/* SCORM courses have no meaningful estimated duration — hide it */}
                            {course.source_type !== 'scorm_imported' && (
                              <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                                <Clock className="w-3 h-3" />
                                <span>{course.estimated_duration?.total ? `${course.estimated_duration.total.hours}h ${course.estimated_duration.total.minutes}m` : 'N/A'}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden md:flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedCourses.includes(course._id)}
                        onChange={() => handleSelectCourse(course._id)}
                        className="mt-1 rounded border-gray-300"
                      />
                      {course.thumbnail_image_url ? (
                        <img
                          src={course.thumbnail_image_url}
                          alt={course.title}
                          className="w-16 h-16 rounded-lg object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      <div className={`w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center text-white ${course.thumbnail_image_url ? 'hidden' : ''}`}>
                        <BookOpen className="w-8 h-8" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 mr-4">
                            <p className="font-medium text-gray-900">{course.title}</p>
                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{course.description}</p>
                            <div className="flex items-center gap-3 mt-2 flex-wrap">
                              {/* SCORM courses have no meaningful estimated duration — hide it */}
                              {course.source_type !== 'scorm_imported' && (
                                <span className="text-xs text-gray-500 flex items-center">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {course.estimated_duration?.total ? `${course.estimated_duration.total.hours}h ${course.estimated_duration.total.minutes}m` : 'N/A'}
                                </span>
                              )}
                              <span className={`text-xs px-2 py-0.5 rounded-full ${course.difficulty?.toLowerCase() === 'beginner' ? 'bg-green-100 text-green-700' :
                                course.difficulty?.toLowerCase() === 'intermediate' ? 'bg-yellow-100 text-yellow-700' :
                                  course.difficulty?.toLowerCase() === 'advanced' ? 'bg-orange-100 text-orange-700' :
                                    'bg-purple-100 text-purple-700'
                                }`}>
                                {course.difficulty?.toUpperCase() || 'N/A'}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 flex-wrap">
                              {course.has_video_content && <span className="flex items-center"><span className="w-2 h-2 bg-blue-400 rounded-full mr-1"></span>Videos</span>}
                              {course.has_audio_content && <span className="flex items-center"><span className="w-2 h-2 bg-green-400 rounded-full mr-1"></span>Audio</span>}
                              {/* <span>Completion: <span className="font-medium">{!isNaN(course.completion_rate) ? Math.round(course.completion_rate) : 0}%</span></span> */}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-xs text-gray-600">
                              <span className="font-medium">{course.module_count}</span> modules
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>

            {/* Course Pagination */}
              <Pagination
                currentPage={coursePagination.page}
                totalPages={coursePagination.totalPages}
                totalItems={coursePagination.totalItems}
                itemsPerPage={coursePagination.limit}
                onPageChange={handleCoursePageChange}
                onItemsPerPageChange={handleCourseLimitChange}
                showItemsPerPage={true}
                itemsPerPageOptions={[10, 20, 50]}
              />
          </div>
        </div>

        {/* Recipients Selection */}
        <div className="space-y-4">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Select {assignmentType === 'department' ? 'Departments' : assignmentType === 'role' ? 'Roles' : 'Recipients'}
              </h2>
              <span className="text-sm text-gray-500">
                {assignmentType === 'department' ? selectedDepartments?.length || 0 : assignmentType === 'role' ? selectedRoles?.length || 0 : selectedUsers?.length || 0} selected
              </span>
            </div>

            {/* Recipient Filters */}
            {assignmentType !== 'bulk' && (
              <div className="space-y-3 mb-4">
                {/* Search Input - Full Width (not for role assignment) */}
                {assignmentType !== 'role' && (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                        placeholder={`Search ${
                          assignmentType === 'department'
                            ? 'departments by name'
                            : 'users by name, email, or employee ID'
                        }...`}
                      value={recipientSearchQuery}
                      onChange={(e) => setRecipientSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                )}

                {/* Department and Role Filters - Below Search */}
                {assignmentType === 'individual' && (
                  <div className="flex gap-3">
                    <Select<FilterFormData>
                      control={control}
                      name="recipientDepartmentFilter"
                      options={[
                        { value: 'all', label: 'All Departments' },
                        ...availableDepartments.map(dept => ({
                          value: dept.group_key,
                          label: dept.group_name
                        }))
                      ]}
                      placeholder="Select Department"
                      isClearable={false}
                    />
                    {recipientDepartmentFilter !== 'all' && availableRolesForDepartment.length > 0 && (
                      <Select<FilterFormData>
                        control={control}
                        name="selectedRoleFilter"
                        options={[
                          { value: 'all', label: 'All Roles' },
                          ...availableRolesForDepartment.map(role => ({
                            value: role.role_key,
                            label: role.role_name
                          }))
                        ]}
                        placeholder="Select Role"
                        isClearable={false}
                      />
                    )}
                  </div>
                )}

                {/* Department and Role Filters for Role Assignment */}
                {assignmentType === 'role' && (
                  <div className="flex gap-3">
                    <Select<FilterFormData>
                      control={control}
                      name="roleAssignmentDepartmentFilter"
                      options={[
                        { value: '', label: 'Select Department' },
                        ...availableDepartments.map(dept => ({
                          value: dept.group_key,
                          label: dept.group_name
                        }))
                      ]}
                      placeholder="Select Department"
                      isClearable={false}
                    />
                    {roleAssignmentDepartmentFilter && roleAssignmentDepartmentFilter !== '' && availableRolesForRoleAssignment.length > 0 && (
                      <Select<FilterFormData>
                        key={`role-select-${roleAssignmentDepartmentFilter}`}
                        control={control}
                        name="roleAssignmentRoleFilter"
                        options={[
                          { value: '', label: 'Select Role' },
                          ...availableRolesForRoleAssignment.map(role => ({
                            value: role.role_key,
                            label: role.role_name
                          }))
                        ]}
                        placeholder="Select Role"
                        isClearable={false}
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            {assignmentType === 'bulk' ? (
              <div className="space-y-6">
                {/* CSV Bulk Import Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-blue-900 mb-2">📤 CSV Bulk Import</h3>
                  <p className="text-sm text-blue-800 mb-3">
                    Import multiple course assignments at once using a CSV file. You can assign courses to users (by email), groups, or roles.
                  </p>
                  <button
                    onClick={() => setShowBulkImportModal(true)}
                    disabled={!canWrite}
                    title={!canWrite ? 'You do not have permission to perform this action' : undefined}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Upload className="w-4 h-4" />
                    Open Bulk Import
                  </button>
                </div>

                {/* Quick Tips */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Quick Tips:</h4>
                  <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                    <li>Use email addresses for USER assignments</li>
                    <li>Use group keys for GROUP assignments (e.g., engineering, sales)</li>
                    <li>Use format <strong>group_key(role_key)</strong> for ROLE assignments (e.g., engineering(manager), sales(admin))</li>
                    <li>You can mix different assignment types in a single CSV</li>
                    <li>The system will validate all entries before importing</li>
                  </ul>
                </div>

                {/* Sample Format */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Sample CSV Format:</h4>
                  <div className="bg-gray-50 p-3 rounded text-xs font-mono overflow-x-auto">
                    <div>type,identifier,course_slug,deadline_days,is_mandatory,message,send_email_notification,enable_automatic_reminder,reminder_frequency_days</div>
                    <div>USER,john@example.com,python-basics,30,true,Please complete,true,true,7</div>
                    <div>GROUP,engineering,leadership-101,60,false,,true,true,14</div>
                    <div>ROLE,sales(sales_rep),product-training,45,true,Important!,true,false,</div>
                  </div>
                </div>
              </div>
            ) : assignmentType === 'department' ? (
              <div className="space-y-4">
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {groupsLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-gray-500">Loading departments...</div>
                    </div>
                  ) : groups.length === 0 ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-gray-500">No departments available</div>
                    </div>
                  ) : (
                    groups.map((group) => (
                      <label
                         key={group.group_key || group._id}
                        className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="department-select"
                          checked={selectedDepartments.includes(group._id)}
                          onChange={() => handleSelectDepartment(group._id)}
                          className="border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Building className="w-5 h-5 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{group.group_name}</p>
                          <div className="flex items-center gap-4 mt-1">
                            <span className="text-xs text-gray-500">
                              Key: {group.group_key}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-1">{group.group_description}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-500">
                            {group.parent_group_key === 'root' ? (
                              <span className="text-blue-600">Root Group</span>
                            ) : (
                              <span>Sub-group</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            Created {new Date(group.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </label>
                    ))
                  )}
                </div>

                {/* Department Pagination */}
                  <Pagination
                    currentPage={groupPagination.page}
                    totalPages={groupPagination.totalPages}
                    totalItems={groupPagination.totalItems}
                    itemsPerPage={groupPagination.limit}
                    onPageChange={handleGroupPageChange}
                    onItemsPerPageChange={handleGroupLimitChange}
                    showItemsPerPage={true}
                    itemsPerPageOptions={[10, 20, 50]}
                  />
              </div>
            ) : assignmentType === 'role' ? (
              <div className="space-y-4">
                {/* Selected Roles Display */}
                {selectedRoles.length > 0 && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium text-gray-700">Selected Roles</h3>
                      <span className="text-xs text-gray-500">{selectedRoles.length} role{selectedRoles.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedRoles.map((role) => {
                        const dept = availableDepartments.find(d => d.group_key === role.group_key);
                        return (
                          <div
                            key={`${role.group_key}_${role.role_key}`}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-full text-sm"
                          >
                            <Briefcase className="w-3.5 h-3.5 text-green-600" />
                            <span className="text-gray-900 font-medium">{role.role_name}</span>
                            <span className="text-gray-400">·</span>
                            <span className="text-gray-500 text-xs">{dept?.group_name || role.group_key}</span>
                            <button
                              onClick={() => handleSelectRole(role.group_key, role.role_key)}
                              className="ml-1 text-gray-400 hover:text-red-600 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center mt-0.5">
                      <span className="text-white text-xs font-bold">i</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-blue-900 font-medium mb-1">How to select roles:</p>
                      <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                        <li>Select a department from the dropdown above</li>
                        <li>Choose a role from that department</li>
                        <li>The role will be added to your selection</li>
                        <li>Repeat to add roles from other departments</li>
                        <li>Click the X button on any role chip to remove it</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {usersLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-gray-500">Loading users...</div>
                    </div>
                  ) : users.length === 0 ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-gray-500">No users available</div>
                    </div>
                  ) : (
                    users.map((user) => (
                      <label
                        key={user._id}
                        className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user._id)}
                          onChange={() => handleSelectUser(user._id)}
                          className="rounded border-gray-300"
                        />
                        <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-semibold">
                          {user.given_name?.[0] || 'U'}{user.family_name?.[0] || ''}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{user.given_name || ''} {user.family_name || ''}</p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {user.employee_id && (
                              <p className="text-xs text-gray-500">{user.employee_id}</p>
                            )}
                            {(() => {
                              // Show departments/groups
                              const userGroups = user.groups || [];

                              if (userGroups.length === 0) return null;

                              if (userGroups.length === 1) {
                                return (
                                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                                    {userGroups[0].group_name}
                                  </span>
                                );
                              }

                              return (
                                <>
                                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                                    {userGroups[0].group_name}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setSelectedUserDepartments({
                                        userName: `${user.given_name || ''} ${user.family_name || ''}`,
                                        email: user.email,
                                        groups: userGroups
                                      });
                                    }}
                                    className="text-xs text-blue-600 font-medium hover:text-blue-800 hover:underline"
                                  >
                                    +{userGroups.length - 1} more
                                  </button>
                                </>
                              );
                            })()}
                            {/* {user.status === 'ACTIVE' ? (
                              <span className="text-xs text-green-600">Active</span>
                            ) : user.status === 'INACTIVE' ? (
                              <span className="text-xs text-gray-600">Inactive</span>
                            ) : user.status === 'SUSPENDED' ? (
                              <span className="text-xs text-red-600">Suspended</span>
                            ) : user.status === 'PENDING_VERIFICATION' ? (
                              <span className="text-xs text-yellow-600">Pending Verification</span>
                            ) : (
                              <span className="text-xs text-gray-500">{user.status || 'Unknown'}</span>
                            )} */}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-500">
                            <div>Assigned: {user.courses?.assigned || 0}</div>
                            <div>Completed: {user.courses?.completed || 0}</div>
                          </div>
                        </div>
                      </label>
                    ))
                  )}
                </div>

                {/* User Pagination */}
                  <Pagination
                    currentPage={userPagination.page}
                    totalPages={userPagination.totalPages}
                    totalItems={userPagination.totalItems}
                    itemsPerPage={userPagination.limit}
                    onPageChange={handleUserPageChange}
                    onItemsPerPageChange={handleUserLimitChange}
                    showItemsPerPage={true}
                    itemsPerPageOptions={[10, 20, 50]}
                  />
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {assignmentType !== 'bulk' && (
            <div className="card bg-gradient-to-br from-indigo-50 to-blue-50">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5 text-indigo-600" />
                  <h3 className="font-semibold text-gray-900">Ready to Assign?</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  {selectedCourses?.length || 0} course(s) will be assigned to{' '}
                  {assignmentType === 'department'
                    ? `${selectedDepartments?.length || 0} department(s)`
                    : assignmentType === 'role'
                      ? `${selectedRoles?.length || 0} role(s)`
                      : `${selectedUsers?.length || 0} user(s)`}
                </p>
                <button
                  onClick={handlePreviewClick}
                  disabled={!canWrite || isCheckingDuplicates || (selectedCourses?.length || 0) === 0 || (assignmentType === 'department' ? (selectedDepartments?.length || 0) === 0 : assignmentType === 'role' ? (selectedRoles?.length || 0) === 0 : (selectedUsers?.length || 0) === 0)}
                  title={!canWrite ? 'You do not have permission to perform this action' : undefined}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCheckingDuplicates ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4" />
                      Preview Assignment
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent Assignments */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Recent Assignments</h2>
          <Link
            to={canViewAnalytics ? ADMIN_COURSE_ASSIGNMENT_ANALYTICS : "#"}
            onClick={(e) => !canViewAnalytics && e.preventDefault()}
            title={!canViewAnalytics ? 'You do not have permission to view analytics' : undefined}
            className={`text-sm ${canViewAnalytics ? 'text-primary-600 hover:text-primary-700' : 'text-gray-400 cursor-not-allowed'}`}
          >
            View All →
          </Link>
        </div>

        {recentAssignmentsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">Loading recent assignments...</div>
          </div>
        ) : !Array.isArray(recentAssignments) || recentAssignments.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">No recent assignments</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Users</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Courses</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Skipped Users</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mandatory</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned By</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deadline Days Count</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(Array.isArray(recentAssignments) ? recentAssignments : []).map((assignment) => (
                  <tr key={assignment._id} className="hover:bg-gray-50">
                    {/* Assigned Users */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-2">
                        {/* Display Users */}
                        {assignment.assigned_to_details?.users?.slice(0, 1).map((user: any) => (
                          <div key={user._id} className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                              {user.name ? user.name.split(' ').map((n: string) => n[0]).join('') : '?'}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{user.name || 'Unknown User'}</p>
                              <p className="text-xs text-gray-500">{user.email || 'N/A'}</p>
                            </div>
                          </div>
                        ))}
                        {assignment.assigned_to_details?.users && assignment.assigned_to_details.users.length > 1 && (
                          <button
                            onClick={() => setSelectedAssignedUsersModal({
                              users: assignment.assigned_to_details.users,
                              type: 'USER'
                            })}
                            className="text-xs text-indigo-600 font-medium hover:text-indigo-800 hover:underline ml-10"
                          >
                            +{assignment.assigned_to_details.users.length - 1} more users
                          </button>
                        )}

                        {/* Display Groups */}
                        {assignment.assigned_to_details?.groups?.slice(0, 1).map((group: any) => (
                          <div key={group._id || group.group_key} className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                              <Users className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">Group: {group.name || group.group_name || 'Unknown Group'}</p>
                              <p className="text-xs text-gray-500">{group.group_key || 'N/A'}</p>
                            </div>
                          </div>
                        ))}
                        {assignment.assigned_to_details?.groups && assignment.assigned_to_details.groups.length > 1 && (
                          <button
                            onClick={() => setSelectedAssignedUsersModal({
                              groups: assignment.assigned_to_details.groups,
                              type: 'GROUP'
                            })}
                            className="text-xs text-indigo-600 font-medium hover:text-indigo-800 hover:underline ml-10"
                          >
                            +{assignment.assigned_to_details.groups.length - 1} more groups
                          </button>
                        )}

                        {/* Display Roles */}
                        {assignment.assigned_to_details?.roles?.slice(0, 1).map((role: any) => (
                          <div key={role._id || role.role_key} className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                              <Shield className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">Role: {role.name || role.role_name || 'Unknown Role'}</p>
                              <p className="text-xs text-gray-500">{role.role_key || 'N/A'}</p>
                            </div>
                          </div>
                        ))}
                        {assignment.assigned_to_details?.roles && assignment.assigned_to_details.roles.length > 1 && (
                          <button
                            onClick={() => setSelectedAssignedUsersModal({
                              roles: assignment.assigned_to_details.roles,
                              type: 'ROLE'
                            })}
                            className="text-xs text-indigo-600 font-medium hover:text-indigo-800 hover:underline ml-10"
                          >
                            +{assignment.assigned_to_details.roles.length - 1} more roles
                          </button>
                        )}
                      </div>
                    </td>

                    {/* Courses */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="text-sm text-gray-900 truncate max-w-[200px]">
                          {assignment.courses?.[0]?.title || 'N/A'}
                        </div>
                        {assignment.courses && assignment.courses.length > 1 && (
                          <button
                            onClick={() => setSelectedCoursesModal({courses: assignment.courses, assignmentId: assignment._id})}
                            className="text-xs text-indigo-600 font-medium hover:text-indigo-800 hover:underline"
                          >
                            +{assignment.courses.length - 1} more
                          </button>
                        )}
                        {assignment.courses && assignment.courses.length === 1 && (
                          <button
                            onClick={() => setSelectedCoursesModal({courses: assignment.courses, assignmentId: assignment._id})}
                            className="p-1 text-gray-400 hover:text-indigo-600 transition-colors"
                            title="View course details"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>

                    {/* Skipped Users */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-2">
                        {/* Check if there are any skipped items */}
                        {(assignment.skipped_users?.length > 0 || assignment.skipped_groups?.length > 0 || assignment.skipped_roles?.length > 0) ? (
                          <>
                            {/* Display first skipped user */}
                            {assignment.skipped_users?.slice(0, 1).map((user: any, index: number) => (
                              <div key={index} className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                  {user.user_name ? user.user_name.split(' ').map((n: string) => n[0]).join('') : '?'}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{user.user_name || 'Unknown User'}</p>
                                  <p className="text-xs text-gray-500">{user.user_email || 'N/A'}</p>
                                </div>
                              </div>
                            ))}
                            {/* Display first skipped group if no users */}
                            {!assignment.skipped_users?.length && assignment.skipped_groups?.slice(0, 1).map((group: any, index: number) => (
                              <div key={index} className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                  <Users className="w-4 h-4" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{group.group_name || 'Unknown Group'}</p>
                                  <p className="text-xs text-gray-500">{group.group_key || 'N/A'}</p>
                                </div>
                              </div>
                            ))}
                            {/* Display first skipped role if no users or groups */}
                            {!assignment.skipped_users?.length && !assignment.skipped_groups?.length && assignment.skipped_roles?.slice(0, 1).map((role: any, index: number) => (
                              <div key={index} className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                  <Shield className="w-4 h-4" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{role.role_name || 'Unknown Role'}</p>
                                  <p className="text-xs text-gray-500">{role.role_key || 'N/A'}</p>
                                </div>
                              </div>
                            ))}
                            {/* Show more button if there are multiple skipped items */}
                            {((assignment.skipped_users?.length || 0) + (assignment.skipped_groups?.length || 0) + (assignment.skipped_roles?.length || 0)) > 1 && (
                              <button
                                onClick={() => setSelectedSkippedUsersModal({
                                  skipped_users: assignment.skipped_users,
                                  skipped_groups: assignment.skipped_groups,
                                  skipped_roles: assignment.skipped_roles
                                })}
                                className="text-xs text-blue-600 font-medium hover:text-blue-800 hover:underline ml-10"
                              >
                                +{((assignment.skipped_users?.length || 0) + (assignment.skipped_groups?.length || 0) + (assignment.skipped_roles?.length || 0)) - 1} more skipped
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="text-sm text-gray-400">None</span>
                        )}
                      </div>
                    </td>

                    {/* Mandatory */}
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        assignment.is_mandatory ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                      }`}>
                        {assignment.is_mandatory ? 'True' : 'False'}
                      </span>
                    </td>

                    {/* Assigned By */}
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{assignment.assigned_by_details?.name || 'Unknown'}</div>
                      <div className="text-xs text-gray-500">{assignment.assigned_by_details?.email || 'N/A'}</div>
                    </td>

                    {/* Deadline */}
                    <td className="px-6 py-4">
                   <div className="text-sm text-gray-900">
                    {assignment.deadline_days
                              ? `${assignment.deadline_days} ${assignment.deadline_days === '1' ? 'day' : 'days'}`
                              : 'N/A'}
                   </div>
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        assignment.status === 'ACTIVE' ? 'bg-green-50 text-green-700' :
                        assignment.status === 'COMPLETED' ? 'bg-blue-50 text-blue-700' :
                        assignment.status === 'CANCELLED' ? 'bg-red-50 text-red-700' :
                        'bg-gray-50 text-gray-700'
                      }`}>
                        {assignment.status}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleSendReminder(assignment._id)}
                        disabled={!canWrite || actionLoading === assignment._id}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={!canWrite ? 'You do not have permission to perform this action' : 'Send Reminder'}
                      >
                        {actionLoading === assignment._id ? (
                          <>
                            <div className="w-4 h-4 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin" />
                            <span>Sending...</span>
                          </>
                        ) : (
                          <>
                            <Mail className="w-4 h-4" />
                            <span>Reminder</span>
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Assignment Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <style>{`
            .modal-scroll::-webkit-scrollbar { width: 5px; }
            .modal-scroll::-webkit-scrollbar-track { background: transparent; }
            .modal-scroll::-webkit-scrollbar-thumb { background: rgba(156, 163, 175, 0.5); border-radius: 10px; }
            .modal-scroll::-webkit-scrollbar-thumb:hover { background: rgba(107, 114, 128, 0.7); }
          `}</style>
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
            <div className="p-6 max-h-[90vh] overflow-y-auto modal-scroll" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(156, 163, 175, 0.5) transparent' }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Assignment Preview</h2>
              <button
                onClick={() => {
                  resetFormState();
                  setShowPreview(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Duplicate Warning Banner */}
              {duplicateCheckResult?.hasConflicts && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-medium text-yellow-900">Duplicate Assignments Detected</h3>
                      <p className="text-sm text-yellow-700 mt-1">
                        {(() => {
                          // Count unique targets (users/departments/roles), not total conflicts
                          const uniqueTargets = new Set(duplicateCheckResult.conflicts.map((c: any) => c.targetId));
                          const targetCount = uniqueTargets.size;
                          const targetLabel = assignmentType === 'department' ? 'department'
                            : assignmentType === 'role' ? 'role'
                            : 'recipient';
                          return (
                            <>
                              {targetCount} {targetLabel}{targetCount > 1 ? 's' : ''}
                              {' '}already {targetCount > 1 ? 'have' : 'has'} some of these courses assigned.
                              {skipDuplicates ? ' Only duplicate courses will be skipped - new courses will still be assigned.' : ' This will create duplicate assignments.'}
                            </>
                          );
                        })()}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Selected Courses */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Selected Courses ({selectedCourses?.length || 0})
                  {skipDuplicates && duplicateCheckResult?.hasConflicts && (() => {
                    const duplicateCourseIds = new Set(
                      duplicateCheckResult.conflicts.map((conflict: any) => conflict.courseId)
                    );
                    const newCoursesCount = selectedCourses.filter(id => !duplicateCourseIds.has(id)).length;
                    return (
                      <span className="ml-2 text-xs text-green-600">
                        ({newCoursesCount} will be assigned, {duplicateCourseIds.size} will be skipped)
                      </span>
                    );
                  })()}
                </h3>
                <div className="space-y-2">
                  {(selectedCourses || []).map(courseId => {
                    const course = courses.find(c => c._id === courseId) || selectedCourseDataRef.current.get(courseId);
                    // Check if this course is a duplicate
                    const isDuplicate = duplicateCheckResult?.conflicts?.some(
                      (conflict: any) => conflict.courseId === courseId
                    );
                    const willBeSkipped = skipDuplicates && isDuplicate;

                    return course ? (
                      <div key={courseId} className={`flex items-center gap-3 p-3 rounded-lg ${willBeSkipped ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'}`}>
                        {course.thumbnail_image_url ? (
                          <img
                            src={course.thumbnail_image_url}
                            alt={course.title}
                            className="w-12 h-12 rounded-lg object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                        ) : null}
                        <div className={`w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center text-white ${course.thumbnail_image_url ? 'hidden' : ''}`}>
                          <BookOpen className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{course.title}</p>
                          <p className="text-xs text-gray-500">
                            {course.estimated_duration?.total ? `${course.estimated_duration.total.hours}h ${course.estimated_duration.total.minutes}m` : 'N/A'} • {course.difficulty?.toUpperCase() || 'N/A'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {willBeSkipped ? (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Will be skipped
                            </span>
                          ) : isDuplicate && !skipDuplicates ? (
                            <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                              Duplicate
                            </span>
                          ) : null}
                          {assignmentSettings.is_mandatory && (
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                              Mandatory
                            </span>
                          )}
                        </div>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>

              {/* Recipients */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  {assignmentType === 'department' ? 'Departments' : assignmentType === 'role' ? 'Roles' : 'Recipients'}
                  {duplicateCheckResult?.hasConflicts && (() => {
                    // Count unique targets (users/departments/roles), not total conflicts
                    const uniqueTargets = new Set(duplicateCheckResult.conflicts.map((c: any) => c.targetId));
                    const targetCount = uniqueTargets.size;
                    const targetLabel = assignmentType === 'department' ? 'department' : assignmentType === 'role' ? 'role' : 'recipient';
                    return (
                      <span className="ml-2 text-xs font-semibold text-yellow-700 bg-yellow-100 px-2 py-1 rounded-full border border-yellow-200">
                        {targetCount} {targetLabel}{targetCount > 1 ? 's' : ''} {targetCount > 1 ? 'have' : 'has'} duplicate courses
                      </span>
                    );
                  })()}
                </h3>
                {assignmentType === 'department' ? (
                  duplicateCheckResult && duplicateCheckResult.hasConflicts ? (
                    <>
                      {/* Departments without conflicts */}
                      {selectedDepartments.filter(deptId => {
                        const hasConflict = duplicateCheckResult?.conflicts?.some(
                          (conflict: any) => conflict.targetId === deptId
                        );
                        return !hasConflict;
                      }).length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm font-semibold text-green-700 mb-3 flex items-center gap-2">
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                              ✅
                            </div>
                            New Assignments (No Duplicates)
                          </p>
                          <div className="space-y-2">
                            {selectedDepartments.filter(deptId => {
                              const hasConflict = duplicateCheckResult?.conflicts?.some(
                                (conflict: any) => conflict.targetId === deptId
                              );
                              return !hasConflict;
                            }).map(deptId => {
                              const dept = groups.find(d => d._id === deptId);
                              return dept ? (
                                <div key={deptId} className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                                      <Building className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div className="flex-1">
                                      <div className="font-medium text-gray-900">{dept.group_name}</div>
                                      <div className="text-sm text-gray-600 mt-0.5">Department</div>
                                    </div>
                                    <div className="text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded">
                                      All New
                                    </div>
                                  </div>
                                </div>
                              ) : null;
                            })}
                          </div>
                        </div>
                      )}

                      {/* Departments with conflicts */}
                      {duplicateCheckResult.conflicts?.length > 0 && selectedDepartments.filter(deptId => {
                        const hasConflict = duplicateCheckResult?.conflicts?.some(
                          (conflict: any) => conflict.targetId === deptId
                        );
                        return hasConflict;
                      }).length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm font-semibold text-yellow-700 mb-3 flex items-center gap-2">
                            <div className="w-5 h-5 rounded-full bg-yellow-100 flex items-center justify-center">
                              ⚠️
                            </div>
                            Has Duplicate Courses (Will receive only NEW courses)
                          </p>
                          <div className="space-y-2">
                            {selectedDepartments.filter(deptId => {
                              const hasConflict = duplicateCheckResult?.conflicts?.some(
                                (conflict: any) => conflict.targetId === deptId
                              );
                              return hasConflict;
                            }).map(deptId => {
                              const dept = groups.find(d => d._id === deptId);
                              if (!dept) return null;

                              const deptConflicts = duplicateCheckResult.conflicts.filter(
                                (conflict: any) => conflict.targetId === deptId
                              );
                              const duplicateCourseNames = deptConflicts.map((conflict: any) => {
                                const course = courses.find(c => c._id === conflict.courseId) || selectedCourseDataRef.current.get(conflict.courseId);
                                return course?.title || 'Unknown Course';
                              });
                              const duplicateCount = deptConflicts.length;
                              const totalCourses = selectedCourses.length;
                              const newCoursesCount = totalCourses - duplicateCount;

                              return (
                                <div key={deptId} className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                                  <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center flex-shrink-0">
                                      <Building className="w-5 h-5 text-yellow-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-gray-900">{dept.group_name}</div>
                                      <div className="text-sm text-gray-600 mt-0.5">Department</div>
                                      <div className="mt-2 space-y-1">
                                        <div className="text-xs text-yellow-800">
                                          <span className="font-semibold">Duplicate courses ({duplicateCount}):</span>
                                          <div className="mt-1 text-yellow-700">{duplicateCourseNames.join(', ')}</div>
                                        </div>
                                        {newCoursesCount > 0 && (
                                          <div className="text-xs text-green-700 font-medium">
                                            ✓ Will receive {newCoursesCount} new course{newCoursesCount > 1 ? 's' : ''}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-xs font-medium text-yellow-700 bg-yellow-100 px-2 py-1 rounded flex-shrink-0">
                                      {duplicateCount}/{totalCourses} Duplicate
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {selectedDepartments.map(deptId  => {
                        const dept = groups.find(d => d._id === deptId);
                        return dept ? (
                          <div key={deptId} className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm">
                            <Building className="w-3 h-3 inline mr-1" />
                            {dept.group_name}
                          </div>
                        ) : null;
                      })}
                    </div>
                  )
                ) : assignmentType === 'role' ? (
                  duplicateCheckResult && duplicateCheckResult.hasConflicts ? (
                    <>
                      {/* Roles without conflicts */}
                      {selectedRoles.filter(roleObj => {
                        const roleId = `${roleObj.group_key}_${roleObj.role_key}`;
                        const hasConflict = duplicateCheckResult?.conflicts?.some(
                          (conflict: any) => conflict.targetId === roleId
                        );
                        return !hasConflict;
                      }).length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm font-semibold text-green-700 mb-3 flex items-center gap-2">
                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                              ✅
                            </div>
                            New Assignments (No Duplicates)
                          </p>
                          <div className="space-y-2">
                            {selectedRoles.filter(roleObj => {
                              const roleId = `${roleObj.group_key}_${roleObj.role_key}`;
                              const hasConflict = duplicateCheckResult?.conflicts?.some(
                                (conflict: any) => conflict.targetId === roleId
                              );
                              return !hasConflict;
                            }).map((roleObj, index) => {
                              const deptName = availableDepartments.find(d => d.group_key === roleObj.group_key)?.group_name || roleObj.group_key;
                              return (
                                <div key={`${roleObj.group_key}-${roleObj.role_key}-${index}`} className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                                      <Briefcase className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div className="flex-1">
                                      <div className="font-medium text-gray-900">{roleObj.role_name || roleObj.role_key}</div>
                                      <div className="text-sm text-gray-600 flex items-center gap-1 mt-0.5">
                                        <Building className="w-3 h-3" />
                                        {deptName}
                                      </div>
                                    </div>
                                    <div className="text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded">
                                      All New
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Roles with conflicts */}
                      {duplicateCheckResult.conflicts?.length > 0 && selectedRoles.filter(roleObj => {
                        const roleId = `${roleObj.group_key}_${roleObj.role_key}`;
                        const hasConflict = duplicateCheckResult?.conflicts?.some(
                          (conflict: any) => conflict.targetId === roleId
                        );
                        return hasConflict;
                      }).length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm font-semibold text-yellow-700 mb-3 flex items-center gap-2">
                            <div className="w-5 h-5 rounded-full bg-yellow-100 flex items-center justify-center">
                              ⚠️
                            </div>
                            Has Duplicate Courses (Will receive only NEW courses)
                          </p>
                          <div className="space-y-2">
                            {selectedRoles.filter(roleObj => {
                              const roleId = `${roleObj.group_key}_${roleObj.role_key}`;
                              const hasConflict = duplicateCheckResult?.conflicts?.some(
                                (conflict: any) => conflict.targetId === roleId
                              );
                              return hasConflict;
                            }).map((roleObj, index) => {
                              const roleId = `${roleObj.group_key}_${roleObj.role_key}`;
                              const roleConflicts = duplicateCheckResult.conflicts.filter(
                                (conflict: any) => conflict.targetId === roleId
                              );
                              const duplicateCourseNames = roleConflicts.map((conflict: any) => {
                                const course = courses.find(c => c._id === conflict.courseId) || selectedCourseDataRef.current.get(conflict.courseId);
                                return course?.title || 'Unknown Course';
                              });
                              const deptName = availableDepartments.find(d => d.group_key === roleObj.group_key)?.group_name || roleObj.group_key;
                              const duplicateCount = roleConflicts.length;
                              const totalCourses = selectedCourses.length;
                              const newCoursesCount = totalCourses - duplicateCount;

                              return (
                                <div key={`${roleObj.group_key}-${roleObj.role_key}-${index}`} className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                                  <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center flex-shrink-0">
                                      <Briefcase className="w-5 h-5 text-yellow-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-gray-900">{roleObj.role_name || roleObj.role_key}</div>
                                      <div className="text-sm text-gray-600 flex items-center gap-1 mt-0.5">
                                        <Building className="w-3 h-3" />
                                        {deptName}
                                      </div>
                                      <div className="mt-2 space-y-1">
                                        <div className="text-xs text-yellow-800">
                                          <span className="font-semibold">Duplicate courses ({duplicateCount}):</span>
                                          <div className="mt-1 text-yellow-700">{duplicateCourseNames.join(', ')}</div>
                                        </div>
                                        {newCoursesCount > 0 && (
                                          <div className="text-xs text-green-700 font-medium">
                                            ✓ Will receive {newCoursesCount} new course{newCoursesCount > 1 ? 's' : ''}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-xs font-medium text-yellow-700 bg-yellow-100 px-2 py-1 rounded flex-shrink-0">
                                      {duplicateCount}/{totalCourses} Duplicate
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {selectedRoles.map((roleObj, index) => (
                        <div key={`${roleObj.group_key}-${roleObj.role_key}-${index}`} className="px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm">
                          <Briefcase className="w-3 h-3 inline mr-1" />
                          {roleObj.role_name || roleObj.role_key}
                          <span className="text-xs text-green-600 ml-1">
                            ({availableDepartments.find(d => d.group_key === roleObj.group_key)?.group_name || roleObj.group_key})
                          </span>
                        </div>
                      ))}
                    </div>
                  )
                ) : (
                  <div>
                    {/* Show categorized users if duplicate check was performed and conflicts exist */}
                    {duplicateCheckResult && duplicateCheckResult.hasConflicts ? (
                      <>
                        {/* Users without conflicts */}
                        <div className="mb-3">
                          <p className="text-xs font-medium text-green-700 mb-2">✅ New Assignments</p>
                          <div className="flex flex-wrap gap-2">
                            {selectedUsers.filter(userId => {
                              // Check if this user has conflicts
                              const hasConflict = duplicateCheckResult?.conflicts?.some(
                                (conflict: any) => conflict.targetId === userId
                              );
                              return !hasConflict;
                            }).map(userId => {
                              const user = getSelectedUser(userId);
                              return user ? (
                                <div key={userId} className="px-3 py-2 bg-green-50 text-green-700 rounded-lg text-sm border border-green-200">
                                  <UserIcon className="w-3 h-3 inline mr-1" />
                                  {user.given_name} {user.family_name || ''}
                                </div>
                              ) : null;
                            })}
                          </div>
                        </div>

                        {/* Users with conflicts */}
                        {/* {duplicateCheckResult.conflicts?.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-medium text-yellow-700 mb-2">⚠️ Has Duplicate Courses (Will receive only NEW courses)</p>
                            <div className="flex flex-wrap gap-2">
                              {selectedUsers.filter(userId => {
                                // Check if this user has conflicts
                                const hasConflict = duplicateCheckResult?.conflicts?.some(
                                  (conflict: any) => conflict.targetId === userId
                                );
                                return hasConflict;
                              }).map(userId => {
                                const user = getSelectedUser(userId);
                                if (!user) return null;

                                // Get courses this user already has
                                const userConflicts = duplicateCheckResult.conflicts.filter(
                                  (conflict: any) => conflict.targetId === userId
                                );
                                const duplicateCourseNames = userConflicts.map((conflict: any) => {
                                  const course = courses.find(c => c._id === conflict.courseId) || selectedCourseDataRef.current.get(conflict.courseId);
                                  return course?.title || 'Unknown Course';
                                });

                                return (
                                  <div key={userId} className="px-3 py-2 bg-yellow-50 text-yellow-700 rounded-lg text-sm border border-yellow-200">
                                    <div className="flex items-start gap-2">
                                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                      <div className="flex-1">
                                        <div className="font-medium">{user.given_name} {user.family_name || ''}</div>
                                        <div className="text-xs text-yellow-600 mb-1">{user.email}</div>
                                        <div className="text-xs text-yellow-800">
                                          <span className="font-semibold">Already has:</span> {duplicateCourseNames.join(', ')}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )} */}
                      </>
                    ) : (
                      /* Show all users normally if no conflicts or check not performed */
                      <div className="flex flex-wrap gap-2">
                        {selectedUsers.map(userId => {
                          const user = getSelectedUser(userId);
                          return user ? (
                            <div key={userId} className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm">
                              <UserIcon className="w-3 h-3 inline mr-1" />
                              {user.given_name} {user.family_name || ''}
                            </div>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Assignment Settings */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Assignment Settings</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="mandatory"
                      className="rounded border-gray-300"
                      checked={assignmentSettings.is_mandatory}
                      onChange={(e) => setAssignmentSettings(prev => ({ ...prev, is_mandatory: e.target.checked }))}
                    />
                    <label htmlFor="mandatory" className="text-sm text-gray-700">Mark as mandatory</label>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Set Deadline</label>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <input
                          type="number"
                          min="1"
                          placeholder="Enter days"
                          className={`w-full px-4 py-2 pr-16 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            deadlineError ? 'border-red-500' : 'border-gray-300'
                          }`}
                          value={deadlineDays !== undefined ? deadlineDays : ''}
                          onChange={handleDeadlineDaysChange}
                          onBlur={handleDeadlineDaysBlur}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none">
                          days
                        </span>
                      </div>
                      {deadlineDays && deadlineDays > 0 && !deadlineError && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                          <Calendar className="w-4 h-4 text-blue-600" />
                          <div>
                            <p className="text-xs text-blue-600 font-medium">
                              {new Date(new Date().setDate(new Date().getDate() + deadlineDays)).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    {deadlineError && (
                      <div className="flex items-center gap-1 text-red-600">
                        <AlertCircle className="w-3 h-3" />
                        <p className="text-xs">{deadlineError}</p>
                      </div>
                    )}
                    {!deadlineError && deadlineDays && deadlineDays > 0 && (
                      <p className="text-xs text-gray-500">
                        Assignment will be due in {deadlineDays} {deadlineDays === 1 ? 'day' : 'days'} from now
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="notify"
                      className="rounded border-gray-300"
                      checked={assignmentSettings.send_notification}
                      onChange={(e) => setAssignmentSettings(prev => ({ ...prev, send_notification: e.target.checked }))}
                    />
                    <label htmlFor="notify" className="text-sm text-gray-700">Send email notification</label>
                  </div>
                  {assignmentSettings.send_notification && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="reminder"
                          className="rounded border-gray-300"
                          checked={assignmentSettings.enable_automatic_reminder}
                          onChange={(e) => setAssignmentSettings(prev => ({ ...prev, enable_automatic_reminder: e.target.checked }))}
                        />
                        <label htmlFor="reminder" className="text-sm text-gray-700">Enable automatic reminders</label>
                      </div>
                      {assignmentSettings.enable_automatic_reminder && (
                        <div className="ml-6 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                          <div className="flex items-center gap-3">
                            <label className="text-sm font-medium text-gray-700">Reminder Frequency:</label>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-600">Every</span>
                              <input
                                type="number"
                                min="1"
                                className="w-16 px-2 py-1.5 text-sm border border-blue-300 rounded-md text-center focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                value={assignmentSettings.reminder_frequency_days || 7}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value) || 1;
                                  setAssignmentSettings(prev => ({ ...prev, reminder_frequency_days: value > 0 ? value : 1 }));
                                }}
                              />
                              <span className="text-sm text-gray-600">day{(assignmentSettings.reminder_frequency_days || 7) !== 1 ? 's' : ''}</span>
                            </div>
                          </div>
                          <div className="flex items-start gap-2 text-xs text-blue-700">
                            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <p>Reminder emails will be sent automatically every {assignmentSettings.reminder_frequency_days || 7} day{(assignmentSettings.reminder_frequency_days || 7) !== 1 ? 's' : ''} to users who haven't completed the assigned course until the deadline or completion.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Message */}
              <Textarea<FilterFormData>
                name="assignmentMessage"
                control={control}
                label="Message (Optional)"
                rows={3}
                placeholder="Add a message for the recipients..."
              />
            </div>

            <div className="flex gap-3 justify-end mt-6 pt-4 border-t">
              <button
                onClick={() => {
                  resetFormState();
                  setShowPreview(false);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignCourses}
                disabled={isAssigning || !!deadlineError}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                {isAssigning ? 'Assigning...' : 'Assign Courses'}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      <BulkImportModal
        isOpen={showBulkImportModal}
        onClose={() => setShowBulkImportModal(false)}
        onImportSuccess={handleBulkImportSuccess}
      />

      {/* Assignment Details Modal */}
      {showAssignmentDetails && selectedAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <style>{`
            .modal-scroll::-webkit-scrollbar { width: 5px; }
            .modal-scroll::-webkit-scrollbar-track { background: transparent; }
            .modal-scroll::-webkit-scrollbar-thumb { background: rgba(156, 163, 175, 0.5); border-radius: 10px; }
            .modal-scroll::-webkit-scrollbar-thumb:hover { background: rgba(107, 114, 128, 0.7); }
          `}</style>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="p-6 max-h-[90vh] overflow-y-auto modal-scroll" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(156, 163, 175, 0.5) transparent' }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Assignment Details</h2>
              <button
                onClick={() => {
                  setShowAssignmentDetails(false);
                  setSelectedAssignment(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Assignment Type */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Type:</span>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${selectedAssignment.assignment_type === 'USER' ? 'bg-blue-100 text-blue-700' :
                  selectedAssignment.assignment_type === 'GROUP' ? 'bg-purple-100 text-purple-700' :
                    selectedAssignment.assignment_type === 'ROLE' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                  }`}>
                  {selectedAssignment.assignment_type}
                </span>
              </div>

              {/* Target Info */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Target Details:</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Assigned By:</span>
                    <span className="text-sm text-gray-900">
                      {selectedAssignment.assigned_by_details
                        ? `${selectedAssignment.assigned_by_details.given_name || ''} ${selectedAssignment.assigned_by_details.family_name || ''}`.trim()
                        : 'Unknown'}
                    </span>

                  </div>
                  <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Assigned To:</span>
                 <div className="text-sm text-gray-900">
                    {selectedAssignment.target_details && selectedAssignment.target_details.length > 0 ? (
                     selectedAssignment.target_details.map((target: any, index: number) => (
                   <span key={getTargetKey(target, index)}
                  className="inline-block bg-gray-100 px-2 py-1 rounded text-xs mr-1 mb-1" >
                  {getTargetName(target, selectedAssignment.assignment_type)}
                  </span>
           ))
         ) : (
      <span className="inline-block bg-gray-100 px-2 py-1 rounded text-xs mr-1 mb-1">
        No targets assigned
      </span>
    )}
             </div>

              </div>
              </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Courses Assigned:</h3>
                <div className="space-y-2">
                   {selectedAssignment.course_details && selectedAssignment.course_details.length > 0 && (selectedAssignment.course_details.map((course: { id: string; title: string }) => (
                <div key={course.id} className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-gray-400" />
           <span className="text-sm text-gray-600">{course.title}</span>
          </div>
         ))
      )
      }
                </div>
              </div>

              {/* Assignment Stats */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Statistics:</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">Total Assignees</div>
                    <div className="text-lg font-semibold text-gray-900">{selectedAssignment.total_assignees || 0}</div>
                  </div>
                  {/* <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">Completion</div>
                    <div className="text-lg font-semibold text-gray-900">{selectedAssignment.completion_percentage || 0}%</div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">Completed</div>
                    <div className="text-lg font-semibold text-green-600">{selectedAssignment.completed_count || 0}</div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">In Progress</div>
                    <div className="text-lg font-semibold text-blue-600">{selectedAssignment.in_progress_count || 0}</div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500">Not Started</div>
                    <div className="text-lg font-semibold text-orange-600">{selectedAssignment.not_started_count || 0}</div>
                  </div> */}
                </div>
              </div>

              {/* Settings */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Settings:</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Mandatory:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedAssignment.is_mandatory ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                      }`}>
                      {selectedAssignment.is_mandatory ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Send Notifications:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedAssignment.send_notification ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                      {selectedAssignment.send_notification ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Auto Reminders:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedAssignment.enable_automatic_reminder ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                      {selectedAssignment.enable_automatic_reminder ? `Every ${selectedAssignment.reminder_frequency_days || 7} days` : 'Disabled'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Status:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedAssignment.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                      selectedAssignment.status === 'CANCELLED' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                      {selectedAssignment.status || 'UNKNOWN'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Custom Message */}
              {selectedAssignment.message && (
                <div className="border-t pt-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Message:</h3>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-700">{selectedAssignment.message}</p>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="border-t pt-4 space-y-1 text-sm text-gray-500">
                <div>Created: {new Date(selectedAssignment.createdAt || Date.now()).toLocaleString()}</div>
                {selectedAssignment.updatedAt && selectedAssignment.updatedAt !== selectedAssignment.createdAt && (
                  <div>Updated: {new Date(selectedAssignment.updatedAt).toLocaleString()}</div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <button
                onClick={() => {
                  setShowAssignmentDetails(false);
                  setSelectedAssignment(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Course Titles Modal */}
      <Modal
        isOpen={!!selectedCoursesModal}
        onClose={() => setSelectedCoursesModal(null)}
        title="Assigned Courses"
        subtitle={selectedCoursesModal ? `${selectedCoursesModal.courses.length} course${selectedCoursesModal.courses.length !== 1 ? 's' : ''} assigned` : ''}
        size="lg"
        maxHeight="80vh"
        footer={
          <div className="flex items-center justify-end">
            <button
              onClick={() => setSelectedCoursesModal(null)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        }
      >
        {selectedCoursesModal && (
          <div className="space-y-3">
            {selectedCoursesModal.courses.map((course, idx) => (
              <div key={course._id || idx} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-sm">
                  {idx + 1}
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{typeof course === 'string' ? course : course.title}</h3>
                </div>
                <BookOpen className="w-5 h-5 text-gray-400" />
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* User Departments & Roles Modal */}
      <Modal
        isOpen={!!selectedUserDepartments}
        onClose={() => setSelectedUserDepartments(null)}
        title="Department & Role Details"
        subtitle={selectedUserDepartments ? `${selectedUserDepartments.userName} (${selectedUserDepartments.email})` : ''}
        size="lg"
        maxHeight="80vh"
        footer={
          <div className="flex items-center justify-end">
            <button
              onClick={() => setSelectedUserDepartments(null)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        }
      >
        {selectedUserDepartments && (
          <div className="space-y-4">
            {selectedUserDepartments.groups.map((group, idx) => (
              <div key={`${group.group_key}-${idx}`} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Department Header */}
                <div className="bg-gray-100 px-4 py-3 flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900">{group.group_name}</h3>
                  </div>
                  <Building className="w-5 h-5 text-gray-500" />
                </div>

                {/* Roles List */}
                {group.roles && group.roles.length > 0 ? (
                  <div className="p-4 space-y-2">
                    <p className="text-xs font-medium text-gray-600 mb-2">Roles in this department:</p>
                    {group.roles.map((role, roleIdx) => (
                      <div key={`${role.role_key}-${roleIdx}`} className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
                        <Briefcase className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-gray-900">{role.role_name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center">
                    <p className="text-xs text-gray-500 italic">No roles assigned in this department</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Delete Assignment"
        description="Are you sure you want to delete this assignment? This action will permanently remove the assignment and cannot be undone."
        itemName={assignmentToDelete?.title}
        isLoading={actionLoading === assignmentToDelete?.id}
      />

      {/* Assigned Users/Groups/Roles Modal for Recent Assignments */}
      <Modal
        isOpen={!!selectedAssignedUsersModal}
        onClose={() => setSelectedAssignedUsersModal(null)}
        title={
          selectedAssignedUsersModal?.type === 'USER' ? 'Assigned Users' :
          selectedAssignedUsersModal?.type === 'GROUP' ? 'Assigned Departments' :
          'Assigned Roles'
        }
        subtitle={
          selectedAssignedUsersModal?.type === 'USER'
            ? `${selectedAssignedUsersModal?.users?.length || 0} user${(selectedAssignedUsersModal?.users?.length || 0) !== 1 ? 's' : ''} assigned`
            : selectedAssignedUsersModal?.type === 'GROUP'
            ? `${selectedAssignedUsersModal?.groups?.length || 0} department${(selectedAssignedUsersModal?.groups?.length || 0) !== 1 ? 's' : ''} assigned`
            : `${selectedAssignedUsersModal?.roles?.length || 0} role${(selectedAssignedUsersModal?.roles?.length || 0) !== 1 ? 's' : ''} assigned`
        }
        size="lg"
        maxHeight="80vh"
        footer={
          <div className="flex items-center justify-end">
            <button
              onClick={() => setSelectedAssignedUsersModal(null)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Close
            </button>
          </div>
        }
      >
        {selectedAssignedUsersModal && (
          <div className="space-y-3">
            {/* Users List */}
            {selectedAssignedUsersModal.type === 'USER' && selectedAssignedUsersModal.users?.map((user: any) => (
              <div key={user._id} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                  {user.name ? user.name.split(' ').map((n: string) => n[0]).join('') : '?'}
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{user.name || 'Unknown User'}</h3>
                  <p className="text-xs text-gray-500">{user.email || 'N/A'}</p>
                </div>
                <UserIcon className="w-5 h-5 text-gray-400" />
              </div>
            ))}

            {/* Groups List */}
            {selectedAssignedUsersModal.type === 'GROUP' && selectedAssignedUsersModal.groups?.map((group: any) => (
              <div key={group._id || group.group_key} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white">
                  <Users className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{group.name || group.group_name || 'Unknown Group'}</h3>
                  <p className="text-xs text-gray-500">{group.group_key || 'N/A'}</p>
                </div>
                <Users className="w-5 h-5 text-gray-400" />
              </div>
            ))}

            {/* Roles List */}
            {selectedAssignedUsersModal.type === 'ROLE' && selectedAssignedUsersModal.roles?.map((role: any) => (
              <div key={role._id || role.role_key} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center text-white">
                  <Shield className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{role.name || role.role_name || 'Unknown Role'}</h3>
                  <p className="text-xs text-gray-500">{role.role_key || 'N/A'}</p>
                </div>
                <Shield className="w-5 h-5 text-gray-400" />
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Skipped Users/Groups/Roles Modal */}
      <Modal
        isOpen={!!selectedSkippedUsersModal}
        onClose={() => setSelectedSkippedUsersModal(null)}
        title="Skipped Assignments"
        subtitle={`${(selectedSkippedUsersModal?.skipped_users?.length || 0) + (selectedSkippedUsersModal?.skipped_groups?.length || 0) + (selectedSkippedUsersModal?.skipped_roles?.length || 0)} item${((selectedSkippedUsersModal?.skipped_users?.length || 0) + (selectedSkippedUsersModal?.skipped_groups?.length || 0) + (selectedSkippedUsersModal?.skipped_roles?.length || 0)) !== 1 ? 's' : ''} skipped due to existing assignments`}
        size="lg"
        maxHeight="80vh"
        footer={
          <div className="flex items-center justify-end">
            <button
              onClick={() => setSelectedSkippedUsersModal(null)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        }
      >
        {selectedSkippedUsersModal && (
          <div className="space-y-3">
            {/* Skipped Users List */}
            {selectedSkippedUsersModal.skipped_users?.map((user: any, index: number) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                  {user.user_name ? user.user_name.split(' ').map((n: string) => n[0]).join('') : '?'}
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{user.user_name || 'Unknown User'}</h3>
                  <p className="text-xs text-gray-500">{user.user_email || 'N/A'}</p>
                  <p className="text-xs text-blue-600 mt-1">{user.reason}</p>
                </div>
                <XCircle className="w-5 h-5 text-blue-500" />
              </div>
            ))}

            {/* Skipped Groups List */}
            {selectedSkippedUsersModal.skipped_groups?.map((group: any, index: number) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white">
                  <Users className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{group.group_name || 'Unknown Group'}</h3>
                  <p className="text-xs text-gray-500">{group.group_key || 'N/A'}</p>
                  <p className="text-xs text-blue-600 mt-1">{group.reason}</p>
                </div>
                <XCircle className="w-5 h-5 text-blue-500" />
              </div>
            ))}

            {/* Skipped Roles List */}
            {selectedSkippedUsersModal.skipped_roles?.map((role: any, index: number) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white">
                  <Shield className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">{role.role_name || 'Unknown Role'}</h3>
                  <p className="text-xs text-gray-500">{role.role_key || 'N/A'}</p>
                  <p className="text-xs text-blue-600 mt-1">{role.reason}</p>
                </div>
                <XCircle className="w-5 h-5 text-blue-500" />
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}